import axios from 'axios';
import { API_URL } from '../../enums/api.enums';

// Response interfaces
export interface Tag {
  id: number;
  name: string;
  description?: string;
  color?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTagRequest {
  name: string;
  description?: string;
  color?: string;
  isActive?: boolean;
}

export interface UpdateTagRequest {
  name?: string;
  description?: string;
  color?: string;
  isActive?: boolean;
}

export interface AssignTagsRequest {
  tagIds: number[];
}

export interface AssignTagsResponse {
  message: string;
  assignedTags: Array<{
    bookId: number;
    tagId: number;
    createdAt: string;
  }>;
}

export interface BookByTag {
  id: number;
  name: string;
  ISBN: string | null;
  description: string;
  country: string;
  language: string;
  size: string;
  amount: number;
  sold: number;
  type: string;
  createdAt: string;
  ebookUrl: string;
  generateId: string;
  pageNumber: number;
  previewAudiobookUrl: string | null;
  previewEbookUrl: string | null;
  priceAudio: number;
  priceEbook: number;
  pricePaper: number;
  isHaveAudio: boolean;
  isHaveEbook: boolean;
  publishingHouseId: number;
  referencyId: string | null;
  releaseDate: string;
  thumbnailUrl: string | null;
  totalStars: number;
  totalVotes: number;
  updatedAt: string;
  userUploadId: number;
  deletedAt: string | null;
  publishingHouse: {
    id: number;
    name: string;
    description: string;
    createdAt: string;
    updatedAt: string;
  };
  authorsBooks: Array<{
    authorId: number;
    bookId: number;
    author: {
      id: number;
      name: string;
      description: string;
      birthYear: number | null;
      createdAt: string;
      updatedAt: string;
    };
  }>;
}

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: `${API_URL.HOST}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use((config) => {
  const raw = localStorage.getItem('token');
  if (raw) {
    let token: string;
    try {
      token = JSON.parse(raw);
    } catch {
      token = raw;
    }
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

class TagsApi {
  private readonly baseUrl = '/tags';

  // 1. Tạo tag mới
  async createTag(data: CreateTagRequest): Promise<Tag> {
    const response = await apiClient.post(this.baseUrl, data);
    return response.data;
  }

  // 2. Lấy danh sách tất cả tags
  async getAllTags(): Promise<Tag[]> {
    const response = await apiClient.get(this.baseUrl);
    return response.data;
  }

  // 3. Lấy tag theo ID
  async getTagById(id: number): Promise<Tag> {
    const response = await apiClient.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // 4. Cập nhật tag
  async updateTag(id: number, data: UpdateTagRequest): Promise<Tag> {
    const response = await apiClient.put(`${this.baseUrl}/${id}`, data);
    return response.data;
  }

  // 5. Xóa tag (soft delete)
  async deleteTag(id: number): Promise<{ message: string }> {
    const response = await apiClient.delete(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // 6. Gán tags cho sách
  async assignTagsToBook(bookId: number, data: AssignTagsRequest): Promise<AssignTagsResponse> {
    const response = await apiClient.post(`${this.baseUrl}/books/${bookId}/assign`, data);
    return response.data;
  }

  // 7. Lấy tags của một sách
  async getTagsByBook(bookId: number): Promise<Tag[]> {
    const response = await apiClient.get(`${this.baseUrl}/books/${bookId}`);
    return response.data;
  }

  // 8. Lấy sách theo tag
  async getBooksByTag(tagId: number): Promise<BookByTag[]> {
    const response = await apiClient.get(`${this.baseUrl}/${tagId}/books`);
    return response.data;
  }

  // 9. Xóa sách khỏi tag
  async removeBookFromTag(tagId: number, bookId: number): Promise<void> {
    await apiClient.delete(`${this.baseUrl}/${tagId}/books/${bookId}`);
  }

  // Lấy danh sách tags đang hoạt động
  async getActiveTags(): Promise<Tag[]> {
    const allTags = await this.getAllTags();
    return allTags.filter(tag => tag.isActive);
  }
}

export const tagsApi = new TagsApi();
